import React, { useMemo, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isNum, num, useFetch, Status, Tile } from './helpers';

// Order sign-off + audit (maker-checker), backend in plus_fund/orders.py. An order is
// logged with a rationale and approved by a DIFFERENT user (approved is final); every
// step and note is kept in an append-only audit trail.
const CCYS = ['AUD', 'USD', 'EUR', 'GBP', 'JPY'];
const STATUS_PILL = {
  pending_approval: ['Pending approval', 'pf-pill-warn'], approved: ['Approved', 'pf-pill-ok'],
rejected: ['Rejected', 'pf-pill-breach'], cancelled: ['Cancelled', 'pf-pill-breach'],
};
const errText = (e) => {
  const d = e.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => `${(x.loc || []).slice(1).join('.')}: ${x.msg}`).join('; ') : (d || e.message);
};
const ts = (s) => (s ? new Date(s).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const numOrNull = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

export const EMPTY_CASH = { line_type: 'cash', cash_action: 'FX', ccy_from: 'AUD', ccy_to: 'USD', amount_ccy: 'AUD', amount_fund: null, amount_sn: null };
const EMPTY_SEC = { line_type: 'security', side: 'BUY', symbol: '', qty_fund: null, qty_sn: null };

// ---- order form (opened from the trade ticket) --------------------------------------
// Indicative cross rate: units of `to` per 1 unit of `from`, from the AUD crosses
// (fxToAud[ccy] = AUD per 1 unit of ccy). null when either leg is not loaded.
export const crossRate = (fxToAud, from, to) => {
  const f = from === 'AUD' ? 1 : fxToAud?.[from];
  const t = to === 'AUD' ? 1 : fxToAud?.[to];
  return isNum(f) && isNum(t) && t !== 0 ? f / t : null;
};
// The other side of an FX line: amount stated in `amount_ccy`, converted at the indicative rate.
export const fxOther = (l, rate) => {
  if (!isNum(rate)) return { ccy: null, fund: null, sn: null };
  const sellSide = l.amount_ccy === l.ccy_from;
  const conv = (a) => (isNum(a) ? (sellSide ? a * rate : a / rate) : null);
  return { ccy: sellSide ? l.ccy_to : l.ccy_from, fund: conv(l.amount_fund), sn: conv(l.amount_sn) };
};
export const fmtRate = (r) => (isNum(r) ? (r >= 10 ? r.toFixed(2) : r.toFixed(4)) : null);

export function OrderForm({ initialLines, nav, snAdj, aplBySym, fxToAud, onDone, onClose }) {
  const [lines, setLines] = useState(initialLines);
  const [rationale, setRationale] = useState('');
  const [tradeDate, setTradeDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const upd = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i) => setLines(lines.filter((_, j) => j !== i));
  // keep the "amount stated in" currency on one of the two legs when a leg changes
  const setLeg = (i, l, patch) => {
    const n = { ...l, ...patch };
    if (n.amount_ccy !== n.ccy_from && n.amount_ccy !== n.ccy_to) n.amount_ccy = n.ccy_from;
    upd(i, n);
  };
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      // FX lines carry the indicative rate (units bought per unit sold) and A$ value at logging
      const payload = lines.map((l) => {
        if (l.line_type !== 'cash') return l;
        const rate = crossRate(fxToAud, l.ccy_from, l.ccy_to);
        const toAud = l.amount_ccy === 'AUD' ? 1 : fxToAud?.[l.amount_ccy];
        return { ...l, fx_rate: rate, est_value_aud: isNum(toAud) && isNum(l.amount_fund) ? l.amount_fund * toAud : null };
      });
      const r = await api.post('/api/plus-fund/orders', { rationale, trade_date: tradeDate, nav_fund: nav, sn_adjustment: snAdj, lines: payload });
      onDone(r.data);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const idx = lines.map((l, i) => [l, i]);
  const secs = idx.filter(([l]) => l.line_type === 'security');
  const fxs = idx.filter(([l]) => l.line_type === 'cash');
  return (
    <div className="pfo-form">
      <div className="pfo-form-head">
        <h3>Log order for approval</h3>
        <button className="pf-link" onClick={onClose}>close</button>
      </div>
      <div className="pf-muted">Each security's full APL record is captured with the order. Once logged, the order cannot be edited: cancel it and log a new one.</div>

      <div className="pfo-sub">Securities <span className="pf-sub">quantities in units for the fund and the SN account; the side gives the direction</span></div>
      <div className="pf-table-scroll">
        <table className="pf-table pf-apl-table pfo-lines">
          <thead><tr><th className="pf-l">Side</th><th className="pf-l">Ticker</th><th className="pf-l">Name</th><th>Qty Fund</th><th>Qty SN</th><th>Price</th><th>FX to AUD</th><th className="pf-l">APL</th><th /></tr></thead>
          <tbody>
            {secs.length === 0 && <tr><td colSpan={9} className="pf-empty">No security lines</td></tr>}
            {secs.map(([l, i]) => (
              <tr key={i}>
                <td><select value={l.side} onChange={(e) => upd(i, { side: e.target.value })}><option>BUY</option><option>SELL</option></select></td>
                <td><input value={l.symbol || ''} style={{ minWidth: 70 }} onChange={(e) => { const s = e.target.value.toUpperCase(); const a = aplBySym[s]; upd(i, { symbol: s, security_id: a?.security_id || null, description: a?.security_description || l.description, currency: a?.currency || l.currency, asset_type: a?.asset_type || l.asset_type }); }} /></td>
                <td className="pf-l pf-sub">{l.description || ''}</td>
                <td className="pf-r"><input className="pf-num" type="number" value={l.qty_fund ?? ''} onChange={(e) => upd(i, { qty_fund: numOrNull(e.target.value) })} /></td>
                <td className="pf-r"><input className="pf-num" type="number" value={l.qty_sn ?? ''} onChange={(e) => upd(i, { qty_sn: numOrNull(e.target.value) })} /></td>
                <td className="pf-r"><input className="pf-num" type="number" step="0.01" value={l.price ?? ''} onChange={(e) => upd(i, { price: numOrNull(e.target.value) })} /></td>
                <td className="pf-r pf-sub">{l.currency === 'AUD' ? '1' : (isNum(l.fx_rate) ? l.fx_rate.toFixed(4) : '')}</td>
                <td className="pf-l">{l.security_id ? <span className="pf-pill pf-pill-ok">{l.security_id}</span> : <span className="pf-pill pf-pill-warn">not in APL</span>}</td>
                <td><button className="pf-link" onClick={() => remove(i)}>remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pfo-sub">FX conversions <span className="pf-sub">changes currency balances (borrowing at IB is a negative cash balance, so it moves with trades and FX); approximate amounts use the latest Datastream rates</span></div>
      <div className="pf-table-scroll">
        <table className="pf-table pf-apl-table pfo-lines">
          <thead><tr><th className="pf-l">Sell</th><th className="pf-l">Buy</th><th className="pf-l">Amount stated in</th><th>Fund amount</th><th>SN amount</th><th>≈ Fund</th><th>≈ SN</th><th className="pf-l">Indicative rate</th><th /></tr></thead>
          <tbody>
            {fxs.length === 0 && <tr><td colSpan={9} className="pf-empty">No FX conversions</td></tr>}
            {fxs.map(([l, i]) => {
              const rate = crossRate(fxToAud, l.ccy_from, l.ccy_to);
              const o = fxOther(l, rate);
              return (
                <tr key={i}>
                  <td><select value={l.ccy_from} onChange={(e) => setLeg(i, l, { ccy_from: e.target.value })}>{CCYS.map((c) => <option key={c}>{c}</option>)}</select></td>
                  <td><select value={l.ccy_to || ''} onChange={(e) => setLeg(i, l, { ccy_to: e.target.value })}>{CCYS.map((c) => <option key={c}>{c}</option>)}</select></td>
                  <td><select value={l.amount_ccy} onChange={(e) => upd(i, { amount_ccy: e.target.value })}>
                    <option value={l.ccy_from}>{l.ccy_from} (amount sold)</option>
                    {l.ccy_to && l.ccy_to !== l.ccy_from && <option value={l.ccy_to}>{l.ccy_to} (amount bought)</option>}
                  </select></td>
                  <td className="pf-r"><input className="pf-num" type="number" value={l.amount_fund ?? ''} onChange={(e) => upd(i, { amount_fund: numOrNull(e.target.value) })} /> <span className="pf-sub">{l.amount_ccy}</span></td>
                  <td className="pf-r"><input className="pf-num" type="number" value={l.amount_sn ?? ''} onChange={(e) => upd(i, { amount_sn: numOrNull(e.target.value) })} /> <span className="pf-sub">{l.amount_ccy}</span></td>
                  <td className="pf-r">{isNum(o.fund) ? `${o.ccy} ${num(o.fund)}` : ''}</td>
                  <td className="pf-r">{isNum(o.sn) ? `${o.ccy} ${num(o.sn)}` : ''}</td>
                  <td className="pf-l pf-sub">{l.ccy_from === l.ccy_to ? 'choose two different currencies' : isNum(rate) ? `1 ${l.ccy_from} = ${fmtRate(rate)} ${l.ccy_to}` : `${l.ccy_from}/${l.ccy_to} rate not loaded`}</td>
                  <td><button className="pf-link" onClick={() => remove(i)}>remove</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pf-toolbar">
        <button className="pf-btn pf-btn-light" onClick={() => setLines([...lines, { ...EMPTY_SEC }])}>Add security line</button>
        <button className="pf-btn pf-btn-light" onClick={() => setLines([...lines, { ...EMPTY_CASH }])}>Add FX conversion</button>
        <label>Trade date <input type="date" value={tradeDate} onChange={(e) => setTradeDate(e.target.value)} /></label>
      </div>
      <label className="pfo-label">Rationale: why this trade is proposed (required, kept in the audit trail)
        <textarea rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} />
      </label>
      {err && <div className="pf-alert">{err}</div>}
      <div className="pf-toolbar">
        <button className="pf-btn" disabled={busy || !rationale.trim() || !lines.length} onClick={submit}>{busy ? 'Logging…' : 'Log order for approval'}</button>
      </div>
    </div>
  );
}

// ---- trade log ------------------------------------------------------------------------
// IB fill status, matched nightly from the IB Activity files (backend reconcile.py)
const FILL_PILL = { filled: ['Filled', 'pf-pill-ok'], partial: ['Partly filled', 'pf-pill-warn'], not_traded: ['Not yet traded', 'pf-pill-navy'] };
const FillPill = ({ s }) => (s && FILL_PILL[s] ? <span className={`pf-pill ${FILL_PILL[s][1]}`}>{FILL_PILL[s][0]}</span> : null);
const fmtD = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '');
function Traded({ p, fx }) {
  if (!p || p.status === 'none') return <span className="pf-sub">–</span>;
  if (p.status === 'not_traded') return <span className="pf-sub">not yet traded</span>;
  const px = isNum(p.avg_price) ? (fx ? fmtRate(p.avg_price) : p.avg_price.toFixed(p.avg_price < 10 ? 4 : 2)) : null;
  return (
    <span className={p.status === 'filled' ? '' : 'pfo-partial'}>
      {num(p.filled)}{px ? ` @ ${px}` : ''}
      <div className="pf-sub">{p.executions} fill{p.executions === 1 ? '' : 's'} · {fmtD(p.first_date)}{p.last_date !== p.first_date ? `–${fmtD(p.last_date)}` : ''}{p.commission ? ` · comm ${num(p.commission, 2)}` : ''}</div>
    </span>
  );
}

function LineTable({ order }) {
  return (
    <table className="pf-table">
      <thead><tr><th className="pf-l">#</th><th className="pf-l">Instruction</th><th className="pf-l">Security</th><th className="pf-l">Class / G-D / FX exp</th><th>Fund</th><th>SN</th><th>Price / FX</th><th>Est A$ (fund)</th><th>Traded fund (IB)</th><th>Traded SN (IB)</th></tr></thead>
      <tbody>
        {order.lines.map((l) => {
          const s = l.apl_snapshot || {};
          const sec = l.line_type === 'security';
          const o = sec ? null : fxOther(l, l.fx_rate);   // fx_rate on an FX line = indicative units bought per unit sold
          const fxAmt = (a, b) => (isNum(a) ? <>{`${l.amount_ccy} ${num(a)}`}{isNum(b) && <div className="pf-sub">≈ {o.ccy} {num(b)}</div>}</> : '–');
          return (
            <tr key={l.id}>
              <td className="pf-l">{l.line_no}</td>
              <td className="pf-l"><strong>{sec ? l.side : `Sell ${l.ccy_from} → buy ${l.ccy_to}`}</strong></td>
              <td className="pf-l">{sec ? <>{l.symbol} <span className="pf-sub">{l.description}</span>{!l.in_apl && <span className="pf-pill pf-pill-warn"> not in APL</span>}<div className="pf-code">{l.security_id} {l.asset_type} {l.currency}{l.multiplier && l.multiplier !== 1 ? ` x${l.multiplier}` : ''}{l.delta && l.delta !== 1 ? ` Δ${l.delta}` : ''}</div></> : <span className="pf-sub">FX conversion, amounts {l.amount_ccy === l.ccy_from ? 'sold' : 'bought'} in {l.amount_ccy}</span>}</td>
              <td className="pf-l pf-sub">{sec ? [s.asset_class, s.sub_asset_class, s.growth_defensive, s.fx_exp && `FX ${s.fx_exp}`, s.source].filter(Boolean).join(' · ') : ''}</td>
              <td className="pf-r">{sec ? (num(l.qty_fund) ?? '–') : fxAmt(l.amount_fund, o.fund)}</td>
              <td className="pf-r">{sec ? (num(l.qty_sn) ?? '–') : fxAmt(l.amount_sn, o.sn)}</td>
              <td className="pf-r">{sec ? `${isNum(l.price) ? l.price : '–'}${isNum(l.fx_rate) && l.currency !== 'AUD' ? ` @ ${l.fx_rate.toFixed(4)}` : ''}` : (isNum(l.fx_rate) ? <span className="pf-sub">1 {l.ccy_from} ≈ {fmtRate(l.fx_rate)} {l.ccy_to}</span> : '')}</td>
              <td className="pf-r">{num(l.est_value_aud) ?? ''}</td>
              <td className="pf-r">{order.status === 'approved' ? <Traded p={l.fills?.fund} fx={!sec} /> : ''}</td>
              <td className="pf-r">{order.status === 'approved' ? <Traded p={l.fills?.sn} fx={!sec} /> : ''}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function OrderDetail({ order, me, selfAllowed, onChanged }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const own = order.created_by === me;
  const act = async (fn) => {
    setBusy(true); setErr(null);
    try { onChanged((await fn()).data); setNote(''); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const decide = (decision) => act(() => api.post(`/api/plus-fund/orders/${order.id}/decision`, { decision, note: note || null }));
  const canCheck = order.status === 'pending_approval' && (!own || selfAllowed);
  return (
    <div className="pfo-detail">
      <div className="pfo-rationale"><span className="pf-sub">Rationale</span><div>{order.rationale}</div></div>
      <div className="pf-table-scroll"><LineTable order={order} /></div>
      <div className="pfo-meta pf-sub">
        Logged by {order.created_by} {ts(order.created_at)}{order.trade_date ? ` · trade date ${order.trade_date}` : ''}
        {isNum(order.nav_fund) ? ` · sized on NAV A$${(order.nav_fund / 1e6).toFixed(1)}m` : ''}{isNum(order.sn_adjustment) ? `, SN adj ${order.sn_adjustment.toFixed(4)}` : ''}
        {order.approved_by ? ` · approved by ${order.approved_by} ${ts(order.approved_at)}` : ''}
        {order.closed_by ? ` · closed by ${order.closed_by} ${ts(order.closed_at)}` : ''}
      </div>
      <div className="pfo-actions">
        <input className="pfo-note" placeholder="Note (required to reject or cancel; kept in the audit trail)" value={note} onChange={(e) => setNote(e.target.value)} />
        {canCheck && <button className="pf-btn" disabled={busy} onClick={() => decide('approve')}>Approve</button>}
        {canCheck && <button className="pf-btn pf-btn-light" disabled={busy || !note.trim()} onClick={() => decide('reject')}>Reject</button>}
        {order.status === 'pending_approval' && own && !selfAllowed && <span className="pf-sub">Awaiting a second person to approve</span>}
        {['pending_approval', 'approved'].includes(order.status) && <button className="pf-btn pf-btn-light" disabled={busy || !note.trim()} onClick={() => decide('cancel')}>Cancel order</button>}
        <button className="pf-btn pf-btn-light" disabled={busy || !note.trim()} onClick={() => act(() => api.post(`/api/plus-fund/orders/${order.id}/notes`, { note }))}>Add note</button>
      </div>
      {err && <div className="pf-alert">{err}</div>}
      <div className="pfo-audit">
        <div className="pf-sub">Audit trail</div>
        <table className="pf-table">
          <tbody>
            {order.events.map((e) => (
              <tr key={e.id}>
                <td className="pf-l pf-sub" style={{ width: 150 }}>{ts(e.at)}</td>
                <td className="pf-l" style={{ width: 110 }}><strong>{e.action}</strong></td>
                <td className="pf-l pf-sub" style={{ width: 220 }}>{e.actor}</td>
                <td className="pf-l pf-sub" style={{ width: 230 }}>{e.from_status && e.from_status !== e.to_status ? `${e.from_status} → ${e.to_status}` : (e.to_status || '')}</td>
                <td className="pf-l" style={{ whiteSpace: 'normal' }}>{e.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TradeLogTab() {
  const [view, setView] = useState('orders');
  const tabs = (
    <div className="pfo-views">
      <button className={`pf-btn ${view === 'orders' ? '' : 'pf-btn-light'}`} onClick={() => setView('orders')}>Signed-off orders</button>
      <button className={`pf-btn ${view === 'history' ? '' : 'pf-btn-light'}`} onClick={() => setView('history')}>Trade history (IB)</button>
      <button className={`pf-btn ${view === 'execution' ? '' : 'pf-btn-light'}`} onClick={() => setView('execution')}>Execution</button>
    </div>
  );
  if (view === 'orders') return <OrderLog views={tabs} />;
  return <>{tabs}{view === 'history' ? <TradeHistory /> : <ExecutionAnalysis />}</>;
}

function OrderLog({ views }) {
  const { user } = useAuth();
  const [status, setStatus] = useState('');
  const log = useFetch('/api/plus-fund/orders', status ? { status } : null);
  const [open, setOpen] = useState(null);
  const orders = useMemo(() => log.data?.orders || [], [log.data]);
  const ib = log.data?.ib_executions;
  const unmatched = log.data?.unmatched_executions || [];
  const pending = orders.filter((o) => o.status === 'pending_approval').length;
  return (
    <>
      {views}
      <div className="pf-note">Every Plus Fund order: logged with a rationale from the trade ticket, and approved by a second person (the author cannot approve their own order). Approved is the signed-off, final state; an approved trade that is not done can still be cancelled with a note. Orders cannot be edited once logged (cancel and log a new one), and the audit trail of every action and note is append-only.</div>
      <div className="pf-toolbar">
        <label>Status <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All</option>{Object.entries(STATUS_PILL).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select></label>
        <button className="pf-btn pf-btn-light" onClick={() => log.reload()}>Refresh</button>
        {pending > 0 && <span className="pf-pill pf-pill-warn">{pending} awaiting approval</span>}
        {log.data?.self_approval_allowed && <span className="pf-pill pf-pill-breach">Self-approval enabled (testing only)</span>}
        {ib && (ib.loaded ? <span className="pf-muted">IB executions last loaded {ts(ib.loaded_at)} · latest IB trade {ib.through || '–'} (nightly from the IB Activity files)</span>
          : <span className="pf-pill pf-pill-warn">IB executions not loaded yet (Plus Fund Scripts/ib_executions_rds.py)</span>)}
      </div>
      <Status loading={log.loading} error={log.error} />
      <div className="pf-table-scroll">
        <table className="pf-table">
          <thead><tr><th className="pf-l">Order</th><th className="pf-l">Status</th><th className="pf-l">Logged</th><th className="pf-l">By</th><th className="pf-l">Lines</th><th className="pf-l">Rationale</th><th className="pf-l">Approved by</th><th className="pf-l">Approved</th><th className="pf-l">IB</th></tr></thead>
          <tbody>
            {orders.length === 0 && !log.loading && <tr><td colSpan={9} className="pf-empty">No orders</td></tr>}
            {orders.map((o) => {
              const [label, cls] = STATUS_PILL[o.status] || [o.status, 'pf-pill-navy'];
              const summary = o.lines.map((l) => (l.line_type === 'security' ? `${l.side} ${l.symbol}` : `FX ${l.ccy_from}→${l.ccy_to}`)).join(', ');
              return (
                <React.Fragment key={o.id}>
                  <tr className="pfo-row" onClick={() => setOpen(open === o.id ? null : o.id)}>
                    <td className="pf-l"><strong>{o.order_ref}</strong></td>
                    <td className="pf-l"><span className={`pf-pill ${cls}`}>{label}</span></td>
                    <td className="pf-l pf-sub">{ts(o.created_at)}</td>
                    <td className="pf-l pf-sub">{o.created_by}</td>
                    <td className="pf-l" style={{ whiteSpace: 'normal', maxWidth: 320 }}>{summary}</td>
                    <td className="pf-l" style={{ whiteSpace: 'normal', maxWidth: 420 }}>{o.rationale}</td>
                    <td className="pf-l pf-sub">{o.approved_by || ''}</td>
                    <td className="pf-l pf-sub">{o.approved_at ? ts(o.approved_at) : ''}</td>
                    <td className="pf-l">{o.status === 'approved' ? <FillPill s={o.fill_status} /> : ''}</td>
                  </tr>
                  {open === o.id && (
                    <tr><td colSpan={9} className="pfo-cell"><OrderDetail order={o} me={user?.email} selfAllowed={log.data?.self_approval_allowed} onChanged={() => log.reload()} /></td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pf-section"><h2>IB trades with no approved order</h2></div>
      <div className="pf-muted">Executions in the fund and SN IB accounts since the first logged order that no approved order accounts for (matched by account, security, side and date; part of a fill beyond an order's quantity shows here too). Each should have an approved order.</div>
      <div className="pf-table-scroll">
        <table className="pf-table">
          <thead><tr><th className="pf-l">Trade date</th><th className="pf-l">Account</th><th className="pf-l">Side</th><th className="pf-l">Security</th><th>Quantity</th><th>Avg price</th><th>Gross</th><th className="pf-l">Ccy</th><th>Fills</th><th>Commission</th></tr></thead>
          <tbody>
            {unmatched.length === 0 && <tr><td colSpan={10} className="pf-empty">{ib?.loaded ? 'None: every IB trade since the first order has an approved order' : 'IB executions not loaded'}</td></tr>}
            {unmatched.map((u, i) => (
              <tr key={i}>
                <td className="pf-l">{u.trade_date}</td>
                <td className="pf-l">{u.account === 'fund' ? 'Fund' : 'SN'}</td>
                <td className="pf-l"><strong>{u.transaction_type}</strong></td>
                <td className="pf-l">{u.symbol} <span className="pf-sub">{u.asset_type === 'CASH' ? 'FX' : u.description}</span></td>
                <td className="pf-r">{num(u.quantity)}</td>
                <td className="pf-r">{isNum(u.avg_price) ? u.avg_price.toFixed(u.avg_price < 10 ? 4 : 2) : ''}</td>
                <td className="pf-r">{num(u.gross)}</td>
                <td className="pf-l pf-sub">{u.currency}</td>
                <td className="pf-r">{u.executions}</td>
                <td className="pf-r">{num(u.commission, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- trade history (IB blotter) -------------------------------------------------------
// Every IB execution in the fund and SN accounts (plus_fund.ib_executions, loaded nightly),
// grouped per day x account x security x side. Read-only and kept apart from the signed-off
// orders: trades before order logging are IB's record, never back-filled as orders.
const isoDaysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const csvCell = (v) => (v === null || v === undefined ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));

export function TradeHistory() {
  const [f, setF] = useState({ start: isoDaysAgo(90), end: isoDaysAgo(0), account: '', symbol: '' });
  const [q, setQ] = useState(f);                     // applied filters (fetch on Apply / Enter)
  const params = { start: q.start, end: q.end, ...(q.account ? { account: q.account } : {}), ...(q.symbol ? { symbol: q.symbol } : {}) };
  const h = useFetch('/api/plus-fund/ib-trades', params);
  const rows = h.data?.rows || [];
  const set = (k, v) => setF({ ...f, [k]: v });
  const apply = () => setQ(f);
  const fxPair = (r) => r.asset_type === 'CASH' && (r.symbol || '').includes('.');
  const download = () => {
    const head = ['trade_date', 'account', 'side', 'symbol', 'description', 'security_id', 'asset_type', 'quantity', 'avg_price', 'gross', 'gross_ccy', 'commission', 'executions'];
    const body = rows.map((r) => [r.trade_date, r.account, r.transaction_type, r.symbol, r.description, r.security_id, r.asset_type,
      r.quantity, r.avg_price, r.gross, fxPair(r) ? r.symbol.split('.')[1] : r.currency, r.commission, r.executions].map(csvCell).join(','));
    const blob = new Blob([[head.join(','), ...body].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `plus_fund_ib_trades_${q.start}_${q.end}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  const tot = rows.reduce((t, r) => ({ fills: t.fills + (r.executions || 0), comm: t.comm + (r.commission || 0) }), { fills: 0, comm: 0 });
  return (
    <>
      <div className="pf-note">Every trade IB executed in the fund and SN accounts, from the daily IB Activity files (one row per day, account, security and side; fills combined). This is IB's record, separate from the signed-off orders: trades before order logging began are not shown as orders.{h.data?.available?.first ? ` Data from ${h.data.available.first} to ${h.data.available.last}, last loaded ${ts(h.data.available.loaded_at)}.` : ''}</div>
      <div className="pf-toolbar" onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}>
        <label>From <input type="date" value={f.start} onChange={(e) => set('start', e.target.value)} /></label>
        <label>To <input type="date" value={f.end} onChange={(e) => set('end', e.target.value)} /></label>
        <label>Account <select value={f.account} onChange={(e) => set('account', e.target.value)}><option value="">Fund and SN</option><option value="fund">Fund</option><option value="sn">SN</option></select></label>
        <label>Security <input type="text" placeholder="ticker, name, ISIN or AUD.USD" value={f.symbol} onChange={(e) => set('symbol', e.target.value)} /></label>
        <button className="pf-btn" onClick={apply}>Apply</button>
        <button className="pf-btn pf-btn-light" disabled={!rows.length} onClick={download}>Download CSV</button>
        {h.data && <span className="pf-muted">{rows.length} rows · {num(tot.fills)} fills · commission {num(tot.comm, 2)}{h.data.truncated ? ' · first 5,000 rows only: narrow the dates' : ''}</span>}
      </div>
      <Status loading={h.loading} error={h.error} />
      <div className="pf-table-scroll">
        <table className="pf-table">
          <thead><tr><th className="pf-l">Trade date</th><th className="pf-l">Account</th><th className="pf-l">Side</th><th className="pf-l">Security</th><th>Quantity</th><th>Avg price</th><th>Gross</th><th className="pf-l">Ccy</th><th>Fills</th><th>Commission</th><th className="pf-l">Time</th></tr></thead>
          <tbody>
            {!h.loading && rows.length === 0 && <tr><td colSpan={11} className="pf-empty">No IB trades in this range</td></tr>}
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="pf-l">{r.trade_date}</td>
                <td className="pf-l">{r.account === 'fund' ? 'Fund' : 'SN'}</td>
                <td className="pf-l"><strong>{r.transaction_type}</strong></td>
                <td className="pf-l">{r.symbol} <span className="pf-sub">{fxPair(r) ? 'FX' : r.description}</span></td>
                <td className="pf-r">{num(r.quantity)}{fxPair(r) && <span className="pf-sub"> {r.symbol.split('.')[0]}</span>}</td>
                <td className="pf-r">{isNum(r.avg_price) ? r.avg_price.toFixed(r.avg_price < 10 ? 4 : 2) : ''}</td>
                <td className="pf-r">{num(r.gross)}</td>
                <td className="pf-l pf-sub">{fxPair(r) ? r.symbol.split('.')[1] : r.currency}</td>
                <td className="pf-r">{r.executions}</td>
                <td className="pf-r">{num(r.commission, 2)}</td>
                <td className="pf-l pf-sub">{r.first_time ? `${r.first_time}${r.last_time && r.last_time !== r.first_time ? `–${r.last_time}` : ''}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- execution analysis -----------------------------------------------------------------
// Each trade (exchange date x account x security x side) against the day's Datastream bar
// (backend execution.py). Slippage is in bp, POSITIVE = COST (bought above / sold below).
const bp = (v) => (isNum(v) ? `${v > 0 ? '+' : ''}${v.toFixed(1)}` : '');
const bpCls = (v) => (!isNum(v) || Math.abs(v) < 0.5 ? '' : v > 0 ? 'pf-neg' : 'pf-pos');
const pctS = (v, dp = 0) => (isNum(v) ? `${(v * 100).toFixed(dp)}%` : '');
const aud = (v) => (isNum(v) ? `${v < 0 ? '-' : ''}A$${num(Math.abs(v))}` : '');

function SummaryTable({ rows, first }) {
  return (
    <table className="pf-table">
      <thead><tr><th className="pf-l">{first}</th><th>Trades</th><th>Gross A$</th><th title="Gross-weighted, positive = cost">vs VWAP bp</th><th>Cost vs VWAP</th><th>Comm + tax bp</th><th>Comm + tax</th><th title="Trades where the fund and SN together were over 25% of the day's volume">&gt;25% of volume</th><th>Flagged</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td className="pf-l">{r.label}</td>
            <td className="pf-r">{r.trades}{r.with_benchmark !== r.trades ? <span className="pf-sub"> ({r.trades - r.with_benchmark} no data)</span> : ''}</td>
            <td className="pf-r">{num(r.gross_aud)}</td>
            <td className={`pf-r ${bpCls(r.slip_vwap_bp)}`}><strong>{bp(r.slip_vwap_bp)}</strong></td>
            <td className={`pf-r ${bpCls(r.cost_vwap_aud)}`}>{aud(r.cost_vwap_aud)}</td>
            <td className="pf-r">{isNum(r.costs_bp) ? r.costs_bp.toFixed(1) : ''}</td>
            <td className="pf-r">{aud(r.costs_aud)}</td>
            <td className="pf-r">{r.high_participation || ''}</td>
            <td className="pf-r">{r.outside_range || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LoggedOrderExecution() {
  // signed-off orders vs the ticket's sizing price (decision price) and approval-to-fill time
  const log = useFetch('/api/plus-fund/orders');
  const rows = [];
  (log.data?.orders || []).filter((o) => o.status === 'approved').forEach((o) => o.lines.forEach((l) => {
    if (l.line_type !== 'security') return;
    ['fund', 'sn'].forEach((acct) => {
      const f = l.fills?.[acct];
      if (!f || f.status === 'none') return;
      const sign = l.side === 'BUY' ? 1 : -1;
      const slip = isNum(f.avg_price) && isNum(l.price) && l.price ? sign * (f.avg_price / l.price - 1) * 1e4 : null;
      const days = f.first_date && o.approved_at ? Math.round((new Date(f.first_date) - new Date(o.approved_at.slice(0, 10))) / 86400000) : null;
      rows.push({ key: `${l.id}-${acct}`, o, l, acct, f, slip, days });
    });
  }));
  return (
    <>
      <div className="pf-section"><h2>Signed-off orders vs decision price</h2></div>
      <div className="pf-muted">Average IB fill against the price the trade ticket sized on (the decision price), so it includes the market move between deciding and trading. Positive = cost.</div>
      <Status loading={log.loading} error={log.error} />
      <div className="pf-table-scroll">
        <table className="pf-table">
          <thead><tr><th className="pf-l">Order</th><th className="pf-l">Account</th><th className="pf-l">Side</th><th className="pf-l">Security</th><th>Ordered</th><th>Filled</th><th>Decision px</th><th>Avg fill</th><th>vs decision bp</th><th className="pf-l">Approved → first fill</th></tr></thead>
          <tbody>
            {rows.length === 0 && !log.loading && <tr><td colSpan={10} className="pf-empty">No approved orders with IB fills yet</td></tr>}
            {rows.map(({ key, o, l, acct, f, slip, days }) => (
              <tr key={key}>
                <td className="pf-l">{o.order_ref}</td><td className="pf-l">{acct === 'fund' ? 'Fund' : 'SN'}</td><td className="pf-l">{l.side}</td>
                <td className="pf-l">{l.symbol}</td><td className="pf-r">{num(f.target)}</td><td className="pf-r">{num(f.filled)}</td>
                <td className="pf-r">{isNum(l.price) ? l.price : ''}</td><td className="pf-r">{isNum(f.avg_price) ? f.avg_price.toFixed(4) : ''}</td>
                <td className={`pf-r ${bpCls(slip)}`}>{bp(slip)}</td>
                <td className="pf-l pf-sub">{f.status === 'not_traded' ? 'not yet traded' : isNum(days) ? `${days} day${days === 1 ? '' : 's'}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function ExecutionAnalysis() {
  const [f, setF] = useState({ start: isoDaysAgo(365), end: isoDaysAgo(0), account: '', symbol: '' });
  const [q, setQ] = useState(f);
  const params = { start: q.start, end: q.end, ...(q.account ? { account: q.account } : {}), ...(q.symbol ? { symbol: q.symbol } : {}) };
  const x = useFetch('/api/plus-fund/execution-analysis', params);
  const d = x.data;
  const set = (k, v) => setF({ ...f, [k]: v });
  const trades = d?.trades || [];
  const download = () => {
    const cols = ['date', 'account', 'side', 'symbol', 'description', 'security_id', 'quantity', 'avg_price', 'gross_aud', 'vwap', 'open', 'prev_close', 'close', 'low', 'high', 'volume',
      'slip_vwap_bp', 'slip_open_bp', 'slip_prev_close_bp', 'slip_close_bp', 'cost_vwap_aud', 'participation', 'range_pos', 'fills', 'chix_share', 'costs_aud', 'costs_bp', 'outside_range'];
    const body = trades.map((t) => cols.map((c) => csvCell(t[c])).join(','));
    const blob = new Blob([[cols.join(','), ...body].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `plus_fund_execution_${q.start}_${q.end}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  const t = d?.total;
  return (
    <>
      <div className="pf-note">Every fund and SN trade (stocks and ETFs; fills combined per exchange date, account, security and side) measured against that day's Datastream bar. Slippage is in basis points and <strong>positive means a cost</strong> (bought above or sold below the benchmark). VWAP is the main yardstick for an order the broker works through the day; the open and prior close show the move from arrival, which is mostly market drift, not execution. Single small trades swing widely with intraday moves, so read the A$-weighted totals. "&gt;25% of volume" counts trades where the fund and SN together were over a quarter of the day's volume (likely price impact). "Flagged" means the average sat outside the day's high-low range: a data or date problem (for example an after-hours fill or a listing mismatch), not execution.</div>
      <div className="pf-toolbar" onKeyDown={(e) => { if (e.key === 'Enter') setQ(f); }}>
        <label>From <input type="date" value={f.start} onChange={(e) => set('start', e.target.value)} /></label>
        <label>To <input type="date" value={f.end} onChange={(e) => set('end', e.target.value)} /></label>
        <label>Account <select value={f.account} onChange={(e) => set('account', e.target.value)}><option value="">Fund and SN</option><option value="fund">Fund</option><option value="sn">SN</option></select></label>
        <label>Security <input type="text" placeholder="ticker, name or ISIN" value={f.symbol} onChange={(e) => set('symbol', e.target.value)} /></label>
        <button className="pf-btn" onClick={() => setQ(f)}>Apply</button>
        <button className="pf-btn pf-btn-light" disabled={!trades.length} onClick={download}>Download CSV</button>
      </div>
      <Status loading={x.loading} error={x.error} />
      {t && (
        <div className="pf-tiles">
          <Tile label="Trades" value={num(t.trades)} sub={`A$${num(t.gross_aud / 1e6, 1)}m gross`} />
          <Tile label="vs VWAP" value={isNum(t.slip_vwap_bp) ? `${bp(t.slip_vwap_bp)} bp` : '–'} sub={`${aud(t.cost_vwap_aud)} ${t.cost_vwap_aud > 0 ? 'cost' : 'saving'}`} />
          <Tile label="Commission + tax" value={isNum(t.costs_bp) ? `${t.costs_bp.toFixed(1)} bp` : '–'} sub={aud(t.costs_aud)} />
          <Tile label="Over 25% of day's volume" value={num(t.high_participation)} sub={`of ${num(t.with_benchmark)} trades with data`} />
          {(t.outside_range > 0 || d.unmapped.length > 0) && <Tile label="Data issues" value={num(t.outside_range)} sub={`flagged${d.unmapped.length ? ` · no Datastream bar: ${d.unmapped.join(', ')}` : ''}`} />}
        </div>
      )}
      {d && (
        <>
          <div className="pf-grid-2">
            <div><div className="pf-section"><h2>By month</h2></div><div className="pf-table-scroll"><SummaryTable first="Month" rows={d.by_month.map((m) => ({ ...m, key: m.month, label: m.month }))} /></div></div>
            <div><div className="pf-section"><h2>By security</h2></div><div className="pf-table-scroll"><SummaryTable first="Security" rows={d.by_security.map((m) => ({ ...m, key: m.symbol, label: <>{m.symbol} <span className="pf-sub">{m.description}</span></> }))} /></div></div>
          </div>
          <div className="pf-section"><h2>Trades</h2></div>
          <div className="pf-table-scroll">
            <table className="pf-table">
              <thead><tr><th className="pf-l">Date</th><th className="pf-l">Acct</th><th className="pf-l">Side</th><th className="pf-l">Security</th><th>Quantity</th><th>Avg price</th><th>VWAP</th><th>vs VWAP bp</th><th>vs open bp</th><th>vs prior close bp</th><th>Cost vs VWAP</th><th title="Fund + SN, same side">% of volume</th><th title="0 = day's low, 100 = day's high">In range</th><th>Fills</th><th>Chi-X</th><th>Comm + tax bp</th></tr></thead>
              <tbody>
                {trades.length === 0 && !x.loading && <tr><td colSpan={16} className="pf-empty">No trades in this range</td></tr>}
                {trades.map((r, i) => (
                  <tr key={i} className={r.outside_range ? 'pfo-flag' : ''} title={r.outside_range ? 'Average price outside the day\u2019s high-low range: a data or date issue, not execution' : ''}>
                    <td className="pf-l">{r.date}</td><td className="pf-l">{r.account === 'fund' ? 'Fund' : 'SN'}</td><td className="pf-l"><strong>{r.side}</strong></td>
                    <td className="pf-l">{r.symbol} <span className="pf-sub">{r.description}</span></td>
                    <td className="pf-r">{num(r.quantity)}</td>
                    <td className="pf-r">{isNum(r.avg_price) ? r.avg_price.toFixed(r.avg_price < 10 ? 4 : 2) : ''}</td>
                    <td className="pf-r">{r.has_market ? (r.vwap < 10 ? r.vwap.toFixed(4) : r.vwap.toFixed(2)) : <span className="pf-sub">no data</span>}</td>
                    <td className={`pf-r ${bpCls(r.slip_vwap_bp)}`}><strong>{bp(r.slip_vwap_bp)}</strong></td>
                    <td className={`pf-r ${bpCls(r.slip_open_bp)}`}>{bp(r.slip_open_bp)}</td>
                    <td className={`pf-r ${bpCls(r.slip_prev_close_bp)}`}>{bp(r.slip_prev_close_bp)}</td>
                    <td className={`pf-r ${bpCls(r.cost_vwap_aud)}`}>{aud(r.cost_vwap_aud)}</td>
                    <td className={`pf-r ${(r.participation || 0) > (d.high_participation || 0.25) ? 'pf-neg' : ''}`}>{pctS(r.participation)}</td>
                    <td className="pf-r">{isNum(r.range_pos) ? Math.round(r.range_pos * 100) : ''}</td>
                    <td className="pf-r">{r.fills}</td>
                    <td className="pf-r">{pctS(r.chix_share)}</td>
                    <td className="pf-r">{isNum(r.costs_bp) ? r.costs_bp.toFixed(1) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.truncated && <div className="pf-muted">First 5,000 trades shown: narrow the dates.</div>}
        </>
      )}
      <LoggedOrderExecution />
    </>
  );
}
